import { Box } from '@mui/material';
import { RowHeights } from '@/shared/constants';
import { floatPercentFormatter } from '@/shared/hooks';
import { getHighlightColorsArray } from '../../model/getHighlightColorArray';
import { ValueType, type Highlight } from '../../model/types';
import { useHighlightMinMaxContext } from '../HighlightMinMaxContext/HighlightMinMaxContext';

interface Props {
  highlight: Highlight;
  rowHeight: RowHeights;
  formatter: (v: number) => string;
  value: number | null | undefined;
  percent?: number;
}

export function Cell({
  highlight,
  rowHeight,
  formatter,
  value,
  percent,
}: Props) {
  const { min, max } = useHighlightMinMaxContext();

  if (value == null) {
    return null;
  }

  const preparedValue = formatter(value);
  const highlightStyles = getHighlightStyles(
    highlight.valueType === ValueType.percent ? percent! : value,
    highlight,
    min,
    max,
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: rowHeight === RowHeights.dense ? 0 : 0.5,
        px: 1.25,
        position: 'relative',
        height: '100%',
        color: highlightStyles.color,

        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: -1,
          backgroundColor: highlightStyles.backgroundColor,
        },
      }}
    >
      <Box sx={{ height: 18, fontSize: '0.875rem', lineHeight: '1.125rem' }}>
        {preparedValue}
      </Box>
      {highlight.valueType === ValueType.percent && percent != null && (
        <Box sx={{ height: 15, fontSize: '0.75rem', lineHeight: '0.875rem' }}>
          {floatPercentFormatter(percent)}
        </Box>
      )}
    </Box>
  );
}

const getHighlightStyles = (
  value: number,
  highlight: Highlight,
  min?: number,
  max?: number,
): { color: string; backgroundColor: string } => {
  const styles = {
    backgroundColor: 'transparent',
    color: 'black',
  };

  if (min == null || max == null) {
    return styles;
  }

  const highlightColorsArray = getHighlightColorsArray(
    min,
    max,
    highlight.levels,
  );

  highlightColorsArray.forEach((highlightLevel, index) => {
    const prevLevelValue =
      index === 0 ? -Infinity : highlightColorsArray[index - 1].value;
    const currentLevelValue =
      index === highlightColorsArray.length - 1
        ? Infinity
        : highlightLevel.value;

    if (value > prevLevelValue && value <= currentLevelValue) {
      styles.backgroundColor = highlightLevel.fillColor;
      styles.color = highlightLevel.textColor;
    }
  });

  return styles;
};
